import React from 'react';
import { Truck, ArrowLeftRight, Clock, Phone } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const AdditionalServices = () => {
  const services = [
    {
      icon: Truck,
      title: 'Towing Services',
      description: 'Stranded or in an accident? Our prompt towing service gets your vehicle safely to our shop.',
      features: [
        '24/7 Emergency Towing',
        'Flatbed & Wheel-Lift',
        'Accident Scene Recovery',
        'Long Distance Available'
      ]
    },
    {
      icon: ArrowLeftRight,
      title: 'Pick Up & Drop Off',
      description: 'We make auto repair convenient with complimentary vehicle pick up and drop off service.',
      features: [
        'Free Local Pick Up',
        'Convenient Drop Off',
        'Flexible Scheduling',
        'GTA Service Area'
      ]
    }
  ];

  return (
    <section className="py-20 bg-[#0a0a0a] relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#ff6b35] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#ff6b35] rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#ff6b35] font-semibold text-sm uppercase tracking-wider mb-3 block">
            Additional Services
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            We Go The Extra Mile
          </h2>
          <div className="h-1 w-24 bg-[#ff6b35] mx-auto rounded-full"></div>
          <p className="text-gray-400 text-lg mt-6 max-w-2xl mx-auto">
            Premium service includes more than just repairs. We offer towing and convenient 
            pick up/drop off to make your experience hassle-free.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Card
                key={index}
                className="bg-gradient-to-br from-[#1a1a1a] to-[#151515] border border-white/10 hover:border-[#ff6b35]/50 transition-all duration-300 group"
              >
                <CardContent className="p-8">
                  {/* Icon */}
                  <div className="w-20 h-20 bg-[#ff6b35]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#ff6b35]/20 transition-colors duration-300">
                    <IconComponent className="w-10 h-10 text-[#ff6b35]" />
                  </div>

                  {/* Title */}
                  <h3 className="text-3xl font-bold text-white mb-4">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-400 mb-6 leading-relaxed text-lg">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-300">
                        <div className="w-2 h-2 bg-[#ff6b35] rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <a
                    href="tel:416-747-6800"
                    className="inline-flex items-center text-[#ff6b35] hover:text-[#e55a28] font-semibold transition-colors duration-200"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call 416-747-6800
                  </a>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom Info */}
        <div className="mt-12 text-center bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-[#ff6b35]" />
            <h4 className="text-white font-semibold text-lg">Available When You Need Us</h4>
          </div>
          <p className="text-gray-400">
            Emergency towing available 24/7 • Pick up & drop off during business hours (Mon-Sat 9AM-6PM)
          </p>
        </div>
      </div>
    </section>
  );
};

export default AdditionalServices;
